import {
  Box,
  Checkbox,
  Flex,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { ReactNode, useEffect, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Button } from './Button';
import { Modal } from './Modal';
import { PersonResultRow } from './PersonResultRow';
import { PickerSectionBox } from './PickerSectionBox';

export type SelectPersonRow = {
  id: string;
  name: string;
  role: string;
  checked: boolean;
  onToggle: () => void;
  ariaLabel?: string;
  disabled?: boolean;
  /** Secondary line shown beneath the name (e.g. an "*external guest" tag). */
  secondaryLabel?: ReactNode;
  /**
   * Escape hatch for an inline editor (e.g. a role text input), shown only while
   * the section's search view is open. The collapsed list always uses the default
   * row so a saved entry reads as static text.
   */
  renderCustom?: () => ReactNode;
};

export type SelectResultRow = { id: string; name: string; email?: string; onAdd: () => void };

export type SelectPeopleSection = {
  key: string;
  title: string;
  addLabel: string;
  searchPlaceholder: string;
  /** Shown when the search matches no one. Omit for a free-text-only section with nothing to search. */
  noneLabel?: string;
  addAsWord?: string;
  allowFreeText?: boolean;
  requiredMessage?: string;
  rows: SelectPersonRow[];
  getResults: (search: string) => SelectResultRow[];
  onAddFreeText?: (typed: string) => void;
  /** Called when Done is clicked; the search view stays open while it returns false. */
  validateBeforeDone?: () => boolean;
};

type SelectPeopleModalProps = {
  opened: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  confirmLabel: string;
  searchConfirmLabel?: string;
  isSaving?: boolean;
  confirmDisabled?: boolean;
  sections: SelectPeopleSection[];
  validationTriggered?: boolean;
  onConfirm: () => void;
  size?: number;
  /**
   * Section to open directly into its search view when the modal opens, instead
   * of the collapsed overview. Used by single-section pickers where the collapsed
   * view would show no list at all.
   */
  defaultOpenSectionKey?: string;
};

const addLinkStyle: React.CSSProperties = {
  color: 'var(--mantine-color-brand-6)',
  fontSize: 12,
  fontWeight: 600,
  textDecorationLine: 'underline',
  textUnderlineOffset: 2,
};

const doneLinkStyle: React.CSSProperties = {
  color: 'var(--mantine-color-brand-6)',
  fontSize: 12,
  fontWeight: 600,
};

function emptyAddSubtitle({ noneLabel, addAsWord }: SelectPeopleSection): string | undefined {
  if (!addAsWord) return noneLabel;
  if (!noneLabel) return `Add as a new ${addAsWord}`;
  return `${noneLabel} — add as a new ${addAsWord}`;
}

export function PersonRow({
  row,
  showInlineEditor = false,
}: {
  row: SelectPersonRow;
  showInlineEditor?: boolean;
}) {
  if (showInlineEditor && row.renderCustom) return <>{row.renderCustom()}</>;
  return (
    <Flex align="center" gap="sm" py="xxxs">
      <Box style={{ width: 160 }}>
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {row.name}
        </Text>
        {row.secondaryLabel}
      </Box>
      <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.6" style={{ flex: 1 }}>
        {row.role}
      </Text>
      <Checkbox
        checked={row.checked}
        onChange={row.onToggle}
        aria-label={row.ariaLabel ?? row.name}
        disabled={row.disabled}
      />
    </Flex>
  );
}

export function SelectPeopleModal({
  opened,
  onClose,
  title,
  subtitle,
  confirmLabel,
  searchConfirmLabel = 'Click done before saving',
  isSaving = false,
  confirmDisabled = false,
  sections,
  validationTriggered = false,
  onConfirm,
  size = 440,
  defaultOpenSectionKey,
}: SelectPeopleModalProps) {
  const [openSectionKey, setOpenSectionKey] = useState<string | null>(
    defaultOpenSectionKey ?? null,
  );
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (opened) return;
    setOpenSectionKey(defaultOpenSectionKey ?? null);
    setSearch('');
  }, [opened, defaultOpenSectionKey]);

  const closeSearch = () => {
    setOpenSectionKey(null);
    setSearch('');
  };

  const handleClose = () => {
    closeSearch();
    onClose();
  };

  const openSection = sections.find(section => section.key === openSectionKey);

  if (openSection) {
    const results = openSection.getResults(search);
    const trimmedSearch = search.trim();
    const showEmptyAdd =
      !!openSection.allowFreeText && trimmedSearch.length > 0 && results.length === 0;
    const showAddAlongsideResults =
      !!openSection.allowFreeText && trimmedSearch.length > 0 && results.length > 0;

    return (
      <Modal opened={opened} onClose={handleClose} size={size} title={title}>
        <PickerSectionBox>
          <Flex align="center" justify="space-between">
            <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
              {openSection.title}
            </Text>
            <UnstyledButton
              style={doneLinkStyle}
              onClick={() => {
                if (openSection.validateBeforeDone?.() === false) return;
                closeSearch();
              }}
            >
              Done
            </UnstyledButton>
          </Flex>

          <Stack gap="xxxs" mt={10}>
            {openSection.rows.map(row => (
              <PersonRow key={row.id} row={row} showInlineEditor />
            ))}
          </Stack>

          <TextInput
            mt="xs"
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
            placeholder={openSection.searchPlaceholder}
            leftSection={<IconSearch size={16} />}
            leftSectionWidth={32}
            styles={{ input: { height: 36, minHeight: 36, borderRadius: 12 } }}
          />

          <Box mt={6}>
            <ScrollArea.Autosize
              mah={260}
              type="never"
              scrollbars="y"
              styles={{ content: { minWidth: 0 } }}
            >
              <Stack gap="xxs" px="xs" py="xxs">
                {results.map(result => (
                  <PersonResultRow
                    key={result.id}
                    title={result.name}
                    subtitle={result.email}
                    actionLabel="Add"
                    onAction={() => {
                      result.onAdd();
                      setSearch('');
                    }}
                  />
                ))}

                {!openSection.allowFreeText && results.length === 0 && (
                  <Text size="sm" c="gray.6">
                    {openSection.noneLabel}
                  </Text>
                )}

                {showAddAlongsideResults && (
                  <PersonResultRow
                    title={trimmedSearch}
                    subtitle={
                      openSection.addAsWord ? `Add as a new ${openSection.addAsWord}` : undefined
                    }
                    actionLabel="Add"
                    onAction={() => {
                      openSection.onAddFreeText?.(trimmedSearch);
                      setSearch('');
                    }}
                  />
                )}

                {showEmptyAdd && (
                  <PersonResultRow
                    title={trimmedSearch}
                    subtitle={emptyAddSubtitle(openSection)}
                    actionLabel="Add"
                    onAction={() => {
                      openSection.onAddFreeText?.(trimmedSearch);
                      setSearch('');
                    }}
                  />
                )}
              </Stack>
            </ScrollArea.Autosize>
          </Box>
        </PickerSectionBox>

        <Box mt="lg">
          <Button fullWidth disabled>
            {searchConfirmLabel}
          </Button>
        </Box>
      </Modal>
    );
  }

  return (
    <Modal opened={opened} onClose={handleClose} size={size} title={title}>
      {subtitle && (
        <Text size="sm" c="dimmed" mb="lg">
          {subtitle}
        </Text>
      )}
      <Stack gap="lg">
        {sections.map(section => (
          <Box key={section.key}>
            <Flex align="center" justify="space-between" px="xxs" pb={10}>
              <Text size="sm" fw={FONT_WEIGHTS.semibold} c="black">
                {section.title}
              </Text>
              <UnstyledButton style={addLinkStyle} onClick={() => setOpenSectionKey(section.key)}>
                {section.addLabel}
              </UnstyledButton>
            </Flex>

            <Stack gap="xxxs" p="xxs">
              {section.rows.map(row => (
                <PersonRow key={row.id} row={row} />
              ))}
            </Stack>

            {validationTriggered && section.requiredMessage && (
              <Text size="sm" fw={FONT_WEIGHTS.regular} c="error.6" mt="xxs">
                {section.requiredMessage}
              </Text>
            )}
          </Box>
        ))}
      </Stack>

      <Box mt="lg">
        <Button
          fullWidth
          onClick={onConfirm}
          loading={isSaving}
          disabled={isSaving || confirmDisabled}
        >
          {confirmLabel}
        </Button>
      </Box>
    </Modal>
  );
}
